import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Popconfirm, Tag, Tooltip } from 'antd';
import CopyToClipboard from 'react-copy-to-clipboard';
import { EditOutlined, DeleteOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { SHIPPING_COMPANY } from '../../../utils/constants';
import { getPackageStatus } from '../../../utils/helpers';
import { Package } from '../../../utils/types';
import message from '../../../components/message';

const PackageCard = ({ pkg, onConfirmDelete }: { pkg: Package, onConfirmDelete: (string) => void}) => {
  const { text: packageStatusText, color: packageStatusColor } = getPackageStatus(pkg);

  const dimFactor = pkg.shippingGroup?.shippingRoute?.dimensionalFactor;
  const zeroDimFactor = pkg.shippingGroup && !dimFactor;

  let dimWeight;

  if (pkg.weight && pkg['length'] && pkg.width && pkg.depth && !zeroDimFactor) {
    const calcDimFactor = dimFactor || 5000;
    const calcDimWeight =
      (Math.ceil(pkg['length']) * Math.ceil(pkg.width) * Math.ceil(pkg.depth)) / calcDimFactor;
    if (calcDimWeight > pkg.weight) {
      dimWeight = calcDimWeight;
    }
  }
  return (
    <Card
      title={
        <div className="Packages-card-title">
          {SHIPPING_COMPANY[pkg.shippingCompany]}&nbsp;
          <a
            href={`https://www.baidu.com/s?wd=${pkg.trackingNumber} 快递`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {pkg.trackingNumber}
          </a>
          <CopyToClipboard
            text={(SHIPPING_COMPANY[pkg.shippingCompany] || '') + ' ' + pkg.trackingNumber}
            onCopy={() => {
              message.success('单号已复制');
            }}
          >
            <span style={{ color: '#666' }}>
              &nbsp;&nbsp;&nbsp;
              <CopyOutlined />
            </span>
          </CopyToClipboard>
        </div>
      }
      bordered={false}
      extra={[
        (pkg.shippingGroup?.status === 'OPEN' ||
          (!pkg.shippingGroup &&
            !pkg?.order?.id &&
            (pkg.status === 'WAREHOUSE_PENDING' || pkg.status === 'WAREHOUSE_RECEIVED'))) && (
          <Link
            to={{
              pathname: `/packages/${pkg.id}/edit`,
              state: {
                package: pkg,
                restrictedEdit: pkg.status === 'WAREHOUSE_RECEIVED',
              },
            }}
            key="edit"
          >
            <EditOutlined key="edit" style={{ color: '#009795' }} />
          </Link>
        ),
        pkg.status === 'WAREHOUSE_PENDING' && (
          <Popconfirm
            key="delete"
            title="确认删除包裹吗？（不可恢复）"
            onConfirm={() => onConfirmDelete(pkg.id)}
            okText="删除"
            cancelText="取消"
          >
            <DeleteOutlined style={{ color: '#a8071a', marginLeft: '15px' }} />
          </Popconfirm>
        ),
        // <EllipsisOutlined key="ellipsis" />,
      ]}
    >
      <div className="Packages-content">
        <div style={{ wordBreak: 'break-all' }}>
          {pkg.items && <div className="Packages-items">物品：{pkg.items}</div>}

          {pkg.notes && <div className="Packages-items">备注：{pkg.notes}</div>}

          {pkg.warehouseNotes && (
            <div className="Packages-items">仓库备注：{pkg.warehouseNotes}</div>
          )}

          {pkg.weight && <div className="Packages-items">入库实重：{pkg.weight.toFixed(2)}kg</div>}
          {dimWeight ? (
            <div className="Packages-items">
              <Tooltip
                title={`该数值并非最终计费重量，是一个预估的参考值。包裹入库尺寸(cm)：${pkg[
                  'length'
                ]?.toFixed(0)}×${pkg['width']?.toFixed(0)}×${pkg['depth']?.toFixed(
                  0,
                )}。尺寸计算根据国际标准是向上取整，如
                                20.1cm 会按 21cm 计算。${
                                  pkg.shippingGroup
                                    ? `包裹所加入的拼邮团体积重计算为体积除以${dimFactor}`
                                    : '该包裹尚未加入拼邮团，计算预估体积重时默认除以5000。选了拼邮团则按该渠道的体积重收费标准进行预估'
                                }。最终打包完体积重和实际重量哪个大按哪个计费`}
              >
                <i>
                  预估体积重： {dimWeight?.toFixed(2)}kg &nbsp;
                  <InfoCircleOutlined />
                </i>
              </Tooltip>
            </div>
          ) : zeroDimFactor && pkg.depth && pkg.width && pkg['length'] ? (
            <div className="Packages-items">
              <Tooltip
                title={`尺寸计算根据国际标准是向上取整，如
                                20.1cm 会按 21cm 计算。该包裹所加入的拼邮团对应的渠道通常不收取体积重费用(详情请查看禁运物品页面的体积重说明)，尺寸数据供参考`}
              >
                <i>
                  入库尺寸：{pkg['length']?.toFixed(0)}×{pkg['width']?.toFixed(0)}×
                  {pkg['depth']?.toFixed(0)}cm&nbsp;
                  <InfoCircleOutlined />
                </i>
              </Tooltip>
            </div>
          ) : null}
        </div>

        <div>
          <Tag color={packageStatusColor}>{packageStatusText}</Tag>
        </div>
      </div>

      {pkg.shippingGroup ? (
        <div className="Packages-content">
          <span style={{ color: '#87d068', fontWeight: 500 }}>拼邮&nbsp;</span>
          <Link to={`/groups/${pkg.shippingGroup.id}`}>
            {pkg.shippingGroup.isPrivate && '🔒 '}
            {pkg && pkg.shippingGroup && pkg.shippingGroup.name}
          </Link>
        </div>
      ) : (
        <div className="Packages-content">
          <span style={{ color: '#2db7f5', fontWeight: 500 }}>直邮&nbsp;</span>
          {pkg?.order?.id && (
            <span>
              订单号：
              <Link to={`/orders`}>{pkg.order.number}</Link>
            </span>
          )}
        </div>
      )}
    </Card>
  );
};

export default React.memo(PackageCard);
